import { normalizeDigits } from './digits.js';
import { normalizeMobileInput } from './masking.js';

/**
 * Input rules for typed codes (FTD §3.3, AC §4.5–§4.6). They only shape input and give early hints;
 * the server is authoritative for every rule and its field errors are shown on the fields.
 */

/** Normalizes Persian/Arabic digits to ASCII and drops spaces and dashes (national code, ID). */
export function normalizeCodeInput(value: string): string {
  return normalizeDigits(value).replace(/[\s-]+/g, '');
}

/**
 * The national-code check digit (AC §4.6): 10 digits; the weighted sum of the first nine (weights
 * 10..2) mod 11 gives the check digit (the remainder if < 2, otherwise 11 − remainder). A hint only.
 */
export function isNationalCodeChecksumValid(code: string): boolean {
  if (!/^\d{10}$/.test(code)) {
    return false;
  }
  const digits = [...code].map(Number);
  const sum = digits.slice(0, 9).reduce((total, digit, index) => total + digit * (10 - index), 0);
  const remainder = sum % 11;
  return remainder < 2 ? digits[9] === remainder : digits[9] === 11 - remainder;
}

/**
 * For a form field: the E.164 mobile ({@link normalizeMobileInput}) or, when the text is not a mobile,
 * the compacted text ({@link normalizeCodeInput}), so the form can validate it against
 * `MOBILE_E164_PATTERN` and still show what was typed.
 */
export function normalizeMobileField(value: string): string {
  return normalizeMobileInput(value) ?? normalizeCodeInput(value);
}

/**
 * A typed Sheba (AC §4.6): Persian or Latin digits, any spaces or dashes, `IR` in any case →
 * `IR` + 24 digits. The API accepts no spaces, so the form sends this compact form.
 */
export function normalizeShebaInput(value: string): string {
  return normalizeDigits(value)
    .replace(/[\s-]+/g, '')
    .toUpperCase();
}

/**
 * The Sheba check digits (ISO 13616 mod 97 on `IR` + 24 digits): a hint only; the server answers
 * `INVALID_CHECKSUM` on `sheba`.
 */
export function isShebaChecksumValid(sheba: string): boolean {
  if (!/^IR\d{24}$/.test(sheba)) {
    return false;
  }
  // Move the country code and check digits to the end; I = 18, R = 27.
  const rearranged = `${sheba.slice(4)}1827${sheba.slice(2, 4)}`;
  let remainder = 0;
  for (const digit of rearranged) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}
